import React, {
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
  Ref,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FabricJSCanvas, useFabricJSEditor } from '../fabricCanvas';
import {
  Circle,
  Rect,
  Textbox,
  loadSVGFromString,
  PencilBrush,
  FabricObject,
  FabricImage,
} from 'fabric';
import Debug from 'debug';

// Components
import { MainButton } from '../buttons/MainButton';
import { SketchTab } from '../AccidentSketch/SketchTab';

// Styled Components
import {
  Container,
  ContainerButton,
  ContainerCanvas,
  SubContainerCanvas,
} from '../AccidentSketch/styles';

// Types
import { SketchProps, ImperativeRefSketch } from './types';

// Hooks
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';

// Constants
import { formActionNaming } from '../../forms/ActionForms/constants';
import { useTheme } from 'styled-components';

const log = Debug('rois:sketch:action');

const ActionSketch = (
  { onSaveDraw, ...props }: SketchProps,
  innerRef: Ref<ImperativeRefSketch>
): JSX.Element => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const canvasRef = useRef<HTMLDivElement>(null);
  const { editor, onReady } = useFabricJSEditor();
  const [hasLoadedSketch, setHasLoadedSketch] = useState(false);

  if (editor) {
    editor.canvas.backgroundColor = `${theme.colors.white}`;
  }

  // T56 FIX: Cargar sketch existente de sessionStorage cuando el editor está listo
  useEffect(() => {
    if (!editor || hasLoadedSketch) return;

    const loadExistingSketch = async () => {
      const svgCanvas = sessionStorage.getItem(formActionNaming.SVG_CANVAS);
      const arrayCanvas = sessionStorage.getItem(formActionNaming.ARRAY_CANVAS);
      const imageCanvas = sessionStorage.getItem(formActionNaming.IMAGE_CANVAS);

      log('Checking sessionStorage for existing sketch:', {
        hasSvg: !!svgCanvas && svgCanvas !== '',
        hasArray: !!arrayCanvas && arrayCanvas !== '[]',
        hasImage: !!imageCanvas,
      });

      // Si hay SVG y objetos, cargar como objetos editables
      if (svgCanvas && svgCanvas !== '' && arrayCanvas && arrayCanvas !== '[]') {
        log('Loading SVG/objects from sessionStorage (full editability)');
        getSvg();
        setHasLoadedSketch(true);
        return;
      }

      // Si solo hay imagen PNG (sketch antiguo), cargar como imagen de fondo
      if (imageCanvas && imageCanvas !== '') {
        log('Loading PNG as background image (old sketch format)');
        try {
          const img = await FabricImage.fromURL(imageCanvas);

          // Escalar la imagen para que quepa en el canvas
          const canvasWidth = editor.canvas.width || 800;
          const canvasHeight = editor.canvas.height || 600;
          const scaleX = canvasWidth / (img.width || canvasWidth);
          const scaleY = canvasHeight / (img.height || canvasHeight);
          const scale = Math.min(scaleX, scaleY);

          img.set({
            scaleX: scale,
            scaleY: scale,
            left: 0,
            top: 0,
            selectable: false, // No se puede seleccionar/mover (es fondo)
            evented: false, // No responde a eventos
          });

          // Añadir como primer objeto (fondo)
          editor.canvas.add(img);
          editor.canvas.sendObjectToBack(img);
          editor.canvas.renderAll();

          log('PNG background image loaded successfully');
          setHasLoadedSketch(true);
        } catch (error) {
          log('Error loading PNG background:', error);
        }
      }
    };

    loadExistingSketch();
  }, [editor, hasLoadedSketch]);

  const onAddCircle = () => {
    if (!editor) return;
    const radius = 50;
    editor.canvas.isDrawingMode = false;
    const circle = new Circle({
      left: ((canvasRef.current?.offsetWidth as number) - radius * 2) / 2,
      top: ((canvasRef.current?.offsetHeight as number) - radius * 2) / 2,
      radius: 50,
      stroke: `${theme.colors.black}`,
      fill: 'transparent',
    });
    editor?.canvas.add(circle);
  };

  const onAddRectangle = () => {
    if (!editor) return;
    editor.canvas.isDrawingMode = false;
    const width = 100;
    const height = 100;
    const rect = new Rect({
      left: ((canvasRef.current?.offsetWidth as number) - width) / 2,
      top: ((canvasRef.current?.offsetHeight as number) - height) / 2,
      width: width,
      height: height,
      stroke: `${theme.colors.black}`,
      fill: 'transparent',
    });
    editor.canvas.add(rect);
  };

  // `useCallback` con `editor` en las dependencias A PROPÓSITO: la identidad de
  // esta función tiene que CAMBIAR cuando fabric termina de montar el canvas,
  // porque es lo que dispara el efecto de `SketchTab` que vuelve a aplicar el
  // modo dibujo. Sin eso, la primera llamada se cae por el `return` de abajo
  // (el editor aún no existe) y nada la reintenta: el canvas se queda en modo
  // SELECCIÓN y el dedo arrastra un rectángulo en vez de pintar.
  const setDraw = useCallback((isEnable: boolean) => {
    if (!editor) return;
    editor.canvas.isDrawingMode = isEnable;

    // Configurar el brush para Fabric.js v6
    if (isEnable && !editor.canvas.freeDrawingBrush) {
      editor.canvas.freeDrawingBrush = new PencilBrush(editor.canvas);
      editor.canvas.freeDrawingBrush.width = 2;
      editor.canvas.freeDrawingBrush.color = `${theme.colors.black}`;
    }
  }, [editor, theme]);

  const removeDraw = () => {
    if (!editor) return;
    editor.deleteAll();
    editor.canvas.isDrawingMode = false;
  };

  const removeFigure = () => {
    if (!editor) return;
    editor.deleteSelected();
  };

  const onAddText = () => {
    if (!editor) return;
    const text = new Textbox(t('sketch.defaultText'), {
      fontSize: 40,
      fontFamily: 'sans-serif',
      editable: true,
      top: 25,
      left: 25,
      height: 200,
    });
    editor?.canvas.add(text);
    editor.canvas.isDrawingMode = false;
  };

  const onSaveDrawClick = () => {
    const imageURL = editor?.canvas.toDataURL();
    const svgCanvas = editor?.canvas.toSVG();
    const arrayObjects = JSON.stringify(editor?.canvas._objects);
    sessionStorage.setItem(formActionNaming.IMAGE_CANVAS, imageURL as string);
    sessionStorage.setItem(formActionNaming.SVG_CANVAS, svgCanvas as string);
    sessionStorage.setItem(
      formActionNaming.ARRAY_CANVAS,
      arrayObjects as string
    );
    onSaveDraw && onSaveDraw();
  };

  const getSvg = () => {
    const objectSvg = sessionStorage.getItem(
      formActionNaming.SVG_CANVAS
    ) as string;
    const arrayObjects: FabricObject[] = JSON.parse(
      sessionStorage.getItem(formActionNaming.ARRAY_CANVAS) as string
    );
    const textBox = arrayObjects.map((object) => object.text);
    loadSVGFromString(objectSvg).then(({ objects }: any) => {
      if (editor) {
        editor.canvas._objects.splice(0, editor.canvas._objects.length);
        const newObj = objects.filter((_: any, index: number) => index !== 0);
        newObj.forEach((object: any, index: number) => {
          if (object.type === 'text') {
            const text = new Textbox(
              textBox[index] || t('sketch.fallbackText'),
              {
                fontSize: 40,
                fontFamily: 'sans-serif',
                top: object.top,
                left: object.left,
                width: object.width,
              }
            );
            editor?.canvas.add(text);
          } else editor.canvas.add(object);
        });
        editor.canvas.renderAll();
      }
    });
  };

  const isLandscapeTable = useIsLandscapeTablet();

  useImperativeHandle(innerRef, () => ({
    getSvg: getSvg,
  }));

  return (
    <Container {...props}>
      <ContainerCanvas>
        <SubContainerCanvas ref={canvasRef}>
          <FabricJSCanvas className="canvas" onReady={onReady} />
        </SubContainerCanvas>
        {!isLandscapeTable && (
          <SketchTab
            onAddCircle={onAddCircle}
            onAddRectangle={onAddRectangle}
            onAddText={onAddText}
            setDraw={setDraw}
            removeAll={removeDraw}
            removeFigure={removeFigure}
          />
        )}
      </ContainerCanvas>
      <ContainerButton>
        {isLandscapeTable && (
          <SketchTab
            onAddCircle={onAddCircle}
            onAddRectangle={onAddRectangle}
            onAddText={onAddText}
            setDraw={setDraw}
            removeAll={removeDraw}
            removeFigure={removeFigure}
          />
        )}
        <MainButton text={t('sketch.save')} onClick={onSaveDrawClick} />
      </ContainerButton>
    </Container>
  );
};

export default forwardRef(ActionSketch);
